/**
 * The audit trail, readable at last.
 *
 * The system has always written a complete record of every privileged action —
 * permission changes, provider credential changes, supervisor interventions,
 * exports, deletions and every recording playback. Nothing in the product could
 * open it. This is that screen.
 *
 * Two choices worth stating:
 *
 * **The filters are the feature.** A reverse-chronological firehose answers no
 * question anyone actually has. The questions are "what did this person do",
 * "what happened to this object" and "who touched recordings last week", so the
 * actor, action and date filters are the primary interface, and one click from
 * any row pivots to that object's full history.
 *
 * **Failures are visible.** A refused privileged action is more interesting
 * than a successful one, so ``outcome != success`` is tinted rather than left
 * to be spotted in a column.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api/client';
import { formatDateTime } from '@/lib/format';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  Panel,
  Row,
} from '@/ui/primitives';

interface AuditEntry {
  id: string;
  created_at: string | null;
  action: string;
  outcome: string;
  actor_id: string | null;
  actor_type: string;
  actor_name: string;
  entity_type: string;
  entity_id: string | null;
  target_user_id: string | null;
  target_user_name: string;
  reason: string;
  ip_address: string;
  correlation_id: string | null;
  metadata: Record<string, unknown>;
}

interface ActionCount {
  action: string;
  count: number;
}

const PAGE = 50;

export function AuditTrail() {
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [actions, setActions] = useState<ActionCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [action, setAction] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [includeAccess, setIncludeAccess] = useState(false);
  const [entity, setEntity] = useState<{ type: string; id: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (entity) {
        const body = await api.get<{ items: AuditEntry[] }>(
          `/audit/entity/${encodeURIComponent(entity.type)}/${encodeURIComponent(entity.id)}`,
        );
        setItems(body.items);
        setHasMore(false);
      } else {
        const query = new URLSearchParams({
          limit: String(PAGE),
          offset: String(offset),
        });
        if (action) query.set('action', action);
        if (start) query.set('start', start);
        if (end) query.set('end', end);
        if (includeAccess) query.set('include_access', 'true');
        const body = await api.get<{ items: AuditEntry[]; has_more: boolean }>(
          `/audit?${query.toString()}`,
        );
        setItems(body.items);
        setHasMore(body.has_more);
      }
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Unable to load the audit trail');
    } finally {
      setLoading(false);
    }
  }, [action, start, end, includeAccess, offset, entity]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api
      .get<{ items: ActionCount[] }>('/audit/actions')
      .then((body) => setActions(body.items))
      .catch(() => setActions([]));
  }, []);

  if (loading && items.length === 0) return <LoadingState label="Loading the audit trail" />;
  if (error) return <ErrorState message={error} onRetry={() => void load()} />;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {entity ? (
          <>
            <Badge tone="brand">
              history of {entity.type} {entity.id.slice(0, 8)}
            </Badge>
            <Button size="sm" onClick={() => setEntity(null)}>
              Back to all activity
            </Button>
          </>
        ) : (
          <>
            <select
              className="h-8 rounded-md border border-line bg-surface px-2 text-sm"
              value={action}
              onChange={(event) => {
                setOffset(0);
                setAction(event.target.value);
              }}
              aria-label="Action"
            >
              <option value="">every action</option>
              {actions.map((row) => (
                <option key={row.action} value={row.action}>
                  {row.action} ({row.count})
                </option>
              ))}
            </select>
            <input
              type="date"
              className="h-8 rounded-md border border-line bg-surface px-2 text-sm"
              value={start}
              onChange={(event) => {
                setOffset(0);
                setStart(event.target.value);
              }}
              aria-label="From date"
            />
            <input
              type="date"
              className="h-8 rounded-md border border-line bg-surface px-2 text-sm"
              value={end}
              onChange={(event) => {
                setOffset(0);
                setEnd(event.target.value);
              }}
              aria-label="To date"
            />
            <label className="flex items-center gap-1 text-sm text-muted">
              <input
                type="checkbox"
                checked={includeAccess}
                onChange={(event) => {
                  setOffset(0);
                  setIncludeAccess(event.target.checked);
                }}
              />
              show who read this log
            </label>
          </>
        )}
        <Button size="sm" className="ml-auto" onClick={() => void load()}>
          Refresh
        </Button>
      </div>

      <Panel
        title="Audit trail"
        actions={<span className="text-2xs text-faint">append-only · cannot be edited</span>}
        className="min-h-0 flex-1"
      >
        {items.length === 0 ? (
          <EmptyState
            message="Nothing recorded for these filters"
            hint="Widen the dates, or choose a different action."
          />
        ) : (
          items.map((entry) => (
            <div key={entry.id}>
              <Row
                className={[
                  'h-auto cursor-pointer py-1.5',
                  entry.outcome !== 'success' ? 'bg-danger-soft/40' : '',
                ].join(' ')}
                onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
              >
                <span className="knx-num w-36 shrink-0 text-sm text-muted">
                  {formatDateTime(entry.created_at)}
                </span>
                <span className="w-44 shrink-0 truncate text-sm font-medium">{entry.action}</span>
                {entry.outcome !== 'success' ? (
                  <Badge tone="danger">{entry.outcome}</Badge>
                ) : null}
                <span className="w-40 shrink-0 truncate text-sm text-muted">
                  {entry.actor_name}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-muted">
                  {entry.target_user_name
                    ? `→ ${entry.target_user_name}`
                    : entry.entity_type
                      ? `${entry.entity_type}`
                      : ''}
                </span>
                {entry.entity_type && entry.entity_id ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(event) => {
                      event.stopPropagation();
                      setEntity({ type: entry.entity_type, id: entry.entity_id as string });
                    }}
                    title="Everything that happened to this object"
                  >
                    History
                  </Button>
                ) : null}
              </Row>

              {expanded === entry.id ? (
                <div className="border-b border-line/60 bg-surface-2/50 px-3 py-2 text-sm">
                  <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-0.5">
                    {entry.reason ? (
                      <>
                        <dt className="text-muted">Reason</dt>
                        <dd>{entry.reason}</dd>
                      </>
                    ) : null}
                    {entry.entity_id ? (
                      <>
                        <dt className="text-muted">Object</dt>
                        <dd className="knx-num break-all">
                          {entry.entity_type} {entry.entity_id}
                        </dd>
                      </>
                    ) : null}
                    {entry.ip_address ? (
                      <>
                        <dt className="text-muted">IP address</dt>
                        <dd className="knx-num">{entry.ip_address}</dd>
                      </>
                    ) : null}
                    {entry.correlation_id ? (
                      <>
                        <dt className="text-muted">Correlation</dt>
                        <dd className="knx-num break-all">{entry.correlation_id}</dd>
                      </>
                    ) : null}
                    {Object.keys(entry.metadata).length > 0 ? (
                      <>
                        <dt className="text-muted">Detail</dt>
                        <dd>
                          <pre className="overflow-x-auto whitespace-pre-wrap break-all text-2xs">
                            {JSON.stringify(entry.metadata, null, 2)}
                          </pre>
                        </dd>
                      </>
                    ) : null}
                  </dl>
                </div>
              ) : null}
            </div>
          ))
        )}
      </Panel>

      {entity ? null : (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            Newer
          </Button>
          <Button size="sm" disabled={!hasMore} onClick={() => setOffset(offset + PAGE)}>
            Older
          </Button>
          <span className="text-2xs text-faint">
            showing {items.length} from {offset + 1}
          </span>
        </div>
      )}
    </div>
  );
}
